import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, MapPin, Navigation, Star, Bookmark, Trash2, Phone, Globe, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
const C = { navy: '#2B4C7E', gold: '#C8A97E', green: '#7FA98C', ink: '#1C252E', mute: '#8D8880', line: '#E3E1DC' };
const inp = 'w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none focus:border-[#2B4C7E]';

const distanceMi = (center, pt) => {
  if (!center || pt?.lat == null || pt?.lng == null) return null;
  const R = 3958.8, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(pt.lat - center.lat), dLng = toRad(pt.lng - center.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(center.lat)) * Math.cos(toRad(pt.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export function FinderFilters({ openNow, setOpenNow, sort, setSort, hasCenter, testid }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`${testid}-filters`}>
      <button onClick={() => setOpenNow(!openNow)} data-testid={`${testid}-opennow`}
        className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${openNow ? 'text-white' : ''}`}
        style={openNow ? { background: C.green, borderColor: C.green } : { borderColor: C.line, color: C.mute }}>
        Open now
      </button>
      <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 px-3 rounded-full border text-xs font-semibold" style={{ borderColor: C.line, color: C.navy }} data-testid={`${testid}-sort`}>
        <option value="best">Best match</option>
        {hasCenter && <option value="near">Nearest first</option>}
        <option value="rating">Top rated</option>
      </select>
    </div>
  );
}

// Reusable "Find a ___ near you" finder (Google Places). Matches the Bank finder UX.
export default function PlaceFinder({
  category, noun = 'places', saveLabel = 'Save',
  placeholder = 'ZIP code or city', savedTitle = 'Saved',
  disclaimer, testid = 'place-finder',
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState([]);
  const [center, setCenter] = useState(null);
  const [openNow, setOpenNow] = useState(false);
  const [sort, setSort] = useState('best');

  const LS_KEY = `finder_last_${category}`;

  const loadSaved = async () => {
    try { const r = await axios.get(`${API}/places/saved`, { ...authH(), params: { category } }); setSaved(r.data.results || []); }
    catch { /* ignore */ }
  };

  const search = async (opts = {}) => {
    setBusy(true); setResults(null); setNote('');
    const usingLocation = !!opts.lat;
    const text = opts.query ?? q;
    try {
      const params = { category, ...opts };
      if (!opts.lat && text) params.query = text;
      const r = await axios.get(`${API}/places/nearby`, { ...authH(), params });
      if (r.data.configured === false) { setNotConfigured(true); setResults([]); }
      else {
        setNotConfigured(false);
        const list = r.data.results || [];
        setCenter(r.data.center || (opts.lat ? { lat: opts.lat, lng: opts.lng } : null));
        setResults(list);
        if (!usingLocation && text) localStorage.setItem(LS_KEY, text);
        const where = usingLocation ? 'near you' : (text ? `near ${text}` : 'near you');
        setNote(list.length ? `${list.length} ${noun} found ${where}` : '');
        if (r.data.message && !list.length) toast.info(r.data.message);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Search failed'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    loadSaved();
    const last = localStorage.getItem(LS_KEY);
    if (last) { setQ(last); search({ query: last }); }
  }, []);

  const useLocation = () => {
    if (!navigator.geolocation) { toast.error('Location not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => search({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error('Could not get your location'),
    );
  };

  const openDetail = async (b) => {
    setDetail({ ...b, loading: true });
    try { const r = await axios.get(`${API}/places/details/${b.place_id}`, authH()); setDetail({ ...b, ...r.data, loading: false }); }
    catch { setDetail({ ...b, loading: false }); }
  };

  const savePlace = async (b) => {
    try {
      await axios.post(`${API}/places/saved`, { category, place_id: b.place_id, name: b.name, address: b.address, phone: b.phone, website: b.website }, authH());
      toast.success('Saved'); loadSaved();
    } catch { toast.error('Could not save'); }
  };
  const removePlace = async (id) => { try { await axios.delete(`${API}/places/saved/${id}`, authH()); toast.success('Removed'); loadSaved(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid={testid}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.mute }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder={placeholder} className={`${inp} pl-9`} data-testid={`${testid}-input`} />
        </div>
        <button onClick={() => search()} disabled={busy} className="h-11 px-5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid={`${testid}-search`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search</button>
        <button onClick={useLocation} className="h-11 px-4 rounded-xl border text-sm font-medium flex items-center justify-center gap-2" style={{ borderColor: C.line, color: C.navy }} data-testid={`${testid}-location`}><Navigation className="h-4 w-4" /> Near me</button>
      </div>

      {notConfigured && <p className="mt-4 text-sm rounded-xl p-3" style={{ background: '#EBF0F5', color: C.navy }}>Live search will be available once the map service is connected.</p>}

      {results && !notConfigured && results.length > 0 && (
        <FinderFilters openNow={openNow} setOpenNow={setOpenNow} sort={sort} setSort={setSort} hasCenter={!!center} testid={testid} />
      )}

      {results && !notConfigured && (() => {
        let list = results.map((b) => ({ ...b, _dist: distanceMi(center, b) }));
        if (openNow) list = list.filter((b) => b.open_now === true);
        if (sort === 'near') list = [...list].sort((a, b) => (a._dist ?? 1e9) - (b._dist ?? 1e9));
        else if (sort === 'rating') list = [...list].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        return (
        <div className="mt-3 space-y-2" data-testid={`${testid}-results`}>
          {note && <p className="text-sm font-semibold" style={{ color: C.navy }} data-testid={`${testid}-count`}>{note}{openNow ? ` · ${list.length} open now` : ''}</p>}
          {list.length === 0 && <p className="text-sm" style={{ color: C.mute }}>{openNow ? 'None open right now — try turning off "Open now".' : 'No results. Try a different ZIP or city.'}</p>}
          {list.map((b) => (
            <div key={b.place_id} className="border border-[#EDE7DC] rounded-xl p-4 flex items-start justify-between gap-3">
              <button onClick={() => openDetail(b)} className="text-left min-w-0 flex-1">
                <p className="font-semibold text-sm" style={{ color: C.ink }}>{b.name}</p>
                <p className="text-xs mt-0.5" style={{ color: C.mute }}>{b.address}</p>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {b.rating != null && <span className="text-xs flex items-center gap-1" style={{ color: C.gold }}><Star className="h-3 w-3 fill-current" /> {b.rating} · {b.user_ratings_total || 0}</span>}
                  {b._dist != null && <span className="text-xs" style={{ color: C.mute }}>{b._dist < 10 ? b._dist.toFixed(1) : Math.round(b._dist)} mi</span>}
                  {b.open_now === true && <span className="text-xs font-semibold" style={{ color: C.green }}>Open now</span>}
                  {b.open_now === false && <span className="text-xs" style={{ color: '#C0563F' }}>Closed</span>}
                </div>
              </button>
              <div className="flex flex-col gap-1.5 shrink-0">
                <a href={b.directions_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Navigation className="h-3.5 w-3.5" /> Directions</a>
                {b.phone && <a href={`tel:${b.phone}`} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }} data-testid={`${testid}-call`}><Phone className="h-3.5 w-3.5" /> Call</a>}
                <button onClick={() => savePlace(b)} className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1" style={{ background: C.green }}><Bookmark className="h-3.5 w-3.5" /> {saveLabel}</button>
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {saved.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: C.mute }}>{savedTitle}</p>
          <div className="space-y-2">
            {saved.map((b) => (
              <div key={b.saved_place_id} className="border border-[#EDE7DC] rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-semibold text-sm truncate" style={{ color: C.ink }}>{b.name}</p><p className="text-xs truncate" style={{ color: C.mute }}>{b.address}</p></div>
                <button onClick={() => removePlace(b.saved_place_id)} className="shrink-0 p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {disclaimer && <p className="mt-4 text-xs" style={{ color: C.mute }}>{disclaimer}</p>}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-detail`}>
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold" style={{ color: C.ink }}>{detail.name}</h3><button onClick={() => setDetail(null)}><X className="h-5 w-5" style={{ color: C.mute }} /></button></div>
            {detail.loading ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.navy }} /></div> : (
              <div className="space-y-3 text-sm" style={{ color: '#4A5560' }}>
                {detail.address && <p className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.navy }} /> {detail.address}</p>}
                {detail.phone && <a href={`tel:${detail.phone}`} className="flex items-center gap-2 font-medium" style={{ color: C.navy }}><Phone className="h-4 w-4" /> {detail.phone}</a>}
                {detail.website && <a href={detail.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium truncate" style={{ color: C.navy }}><Globe className="h-4 w-4 shrink-0" /> Visit website</a>}
                <div className="flex gap-2 pt-2">
                  <a href={detail.directions_url || detail.maps_url} target="_blank" rel="noreferrer" className="flex-1 text-center text-sm px-3 py-2 rounded-xl border font-medium" style={{ borderColor: C.line, color: C.navy }}>Directions</a>
                  <button onClick={() => { savePlace(detail); setDetail(null); }} className="flex-1 text-sm px-3 py-2 rounded-xl text-white font-medium" style={{ background: C.green }}>{saveLabel}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
